import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import useSeo from "@/lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  "Pazaryeri mağaza kurulumu",
  "Mevcut mağaza yönetimi",
  "Fiyat ve stok entegrasyonu",
  "Web ve mobil sipariş sistemi",
  "Kurye çözümü",
  "Tüm hizmetler",
  "Henüz karar vermedim",
];

const SECTOR_OPTIONS = [
  "Market",
  "Restoran",
  "Kasap / Şarküteri",
  "Manav",
  "Petshop",
  "Çiçekçi",
  "Diğer",
];

const CHANNEL_OPTIONS = [
  "Uber Eats Trendyol Go",
  "Getir Çarşı",
  "Getir Yemek",
  "Yemeksepeti Mahalle",
  "Yemeksepeti",
  "Migros Yemek",
  "Kendi web sitem",
  "Henüz yok",
];

const SERVICE_OPTIONS = [
  "Pazaryeri Kurulum ve Yönetimi",
  "İşyerine Özel Sipariş Sistemi",
  "Kurye Çözümü",
  "Fiyat ve Stok Entegrasyonu",
  "Ürün ve Menü Yönetimi",
  "Dijital Operasyon Yönetimi",
];

const PAYMENT_OPTIONS = [
  "Online ödeme",
  "Kapıda nakit",
  "Kapıda kartla",
];

const initial = {
  interest: "",
  fullName: "",
  memberBusinessName: "",
  phone: "",
  email: "",
  city: "",
  district: "",
  sector: "",
  currentChannels: [],
  services: [],
  approxProducts: "",
  approxDailyOrders: "",
  description: "",
  avgDeliveryDistance: "",
  dailyCourierNeed: "",
  courierOrderChannels: [],
  hasWebsite: "",
  wantsMobileApp: "",
  paymentMethods: [],
  needsCourier: "",
  kvkkConsent: false,
  commercialConsent: false,
  website_hp: "",
};

export default function Application() {
  useSeo({
    title: "Ücretsiz Üye İşyeri Analizi | Epersonel",
    description:
      "İşyerinize özel ücretsiz ön analiz alın. Mevcut satış kanallarınızı, ürün yapınızı ve teslimat ihtiyacınızı değerlendirelim; size uygun çözümleri belirleyelim.",
  });
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleArr = (k, v) =>
    setData((d) => ({
      ...d,
      [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v],
    }));

  const needsCourierBranch = data.services.includes("Kurye Çözümü") || data.interest === "Kurye çözümü";
  const needsWebMobileBranch =
    data.services.includes("İşyerine Özel Sipariş Sistemi") ||
    data.interest === "Web ve mobil sipariş sistemi";
  const showBranches = needsCourierBranch || needsWebMobileBranch;

  const steps = [
    { key: "interest", label: "İlgi Alanı" },
    { key: "main", label: "İşyeri Bilgileri" },
    ...(showBranches ? [{ key: "branch", label: "Detaylar" }] : []),
    { key: "consent", label: "Onay" },
  ];

  const validateStep = () => {
    const e = {};
    if (step === 0 && !data.interest) e.interest = "Bir seçenek seçin.";
    if (step === 1) {
      if (!data.fullName || data.fullName.trim().length < 2)
        e.fullName = "Ad soyad zorunludur.";
      if (!data.memberBusinessName || data.memberBusinessName.trim().length < 2)
        e.memberBusinessName = "İşyeri adı zorunludur.";
      const phoneClean = data.phone.replace(/\s+/g, "");
      const trPhone = /^(?:\+?90|0)?\s?\(?5\d{2}\)?[\s.-]?\d{3}[\s.-]?\d{2}[\s.-]?\d{2}$/;
      if (!trPhone.test(phoneClean))
        e.phone = "Geçerli bir Türkiye cep telefonu numarası girin.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email))
        e.email = "Geçerli bir e-posta adresi girin.";
      if (!data.city) e.city = "İl bilgisi zorunludur.";
      if (!data.sector) e.sector = "Sektör seçin.";
    }
    const isConsentStep = step === steps.length - 1;
    if (isConsentStep && !data.kvkkConsent)
      e.kvkkConsent = "KVKK onayı zorunludur.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/applications`, data, { timeout: 20000 });
      setSubmitted(true);
      if (res.data?.emailConfigured === false) {
        toast.success("Başvurunuz alındı. Ekibimiz en kısa sürede sizinle iletişime geçecektir.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Bir Hata Oluştu. Lütfen tekrar deneyin.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="min-h-[60vh] py-20">
        <div className="ep-container max-w-2xl text-center">
          <div className="w-16 h-16 mx-auto rounded-full grid place-items-center bg-[#00F22E]/15 text-[#00F22E] mb-6">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="application-success">
            Başvurunuz Alındı
          </h1>
          <p className="mt-4 text-white/85 leading-relaxed">
            Başvurunuz bize ulaştı. Ekibimiz en kısa sürede sizinle iletişime
            geçecektir.
          </p>
          <Link to="/" className="btn-primary mt-8" data-testid="application-home">
            Ana Sayfaya Dön
          </Link>
        </div>
      </section>
    );
  }

  const isLast = step === steps.length - 1;

  return (
    <section className="py-14 md:py-20">
      <div className="ep-container max-w-2xl">
        <p className="text-xs tracking-[0.3em] text-[#00F22E] font-medium mb-3">
          ÜCRETSİZ ANALİZ AL
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
          İşyeriniz İçin Ücretsiz Ön Analiz Alın
        </h1>
        <p className="mt-4 text-muted leading-relaxed">
          Mevcut satış kanallarınızı, ürün yapınızı, fiyat-stok sisteminizi ve
          teslimat ihtiyacınızı değerlendirelim. Size uygun çözümleri belirleyerek
          yükümlülük oluşturmayan bir ön değerlendirme sunalım.
        </p>

        {/* Progress */}
        <div className="mt-8 flex gap-2" aria-label="Form ilerlemesi">
          {steps.map((s, i) => (
            <div
              key={s.key}
              className={`flex-1 h-1 rounded-full ${i <= step ? "bg-[#00F22E]" : "bg-white/[0.08]"}`}
              data-testid={`progress-${i}`}
            />
          ))}
        </div>

        <form
          className="mt-8 ep-card p-6 md:p-8"
          data-testid={`form-step-${step}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (isLast) {
              submit();
            } else {
              next();
            }
          }}
          noValidate
          aria-label="Ücretsiz analiz başvuru formu"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website_hp"
            value={data.website_hp}
            onChange={(e) => set("website_hp", e.target.value)}
            tabIndex="-1"
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          {step === 0 && (
            <fieldset>
              <legend className="font-display text-xl font-semibold mb-6">
                Hangi çözümle ilgileniyorsunuz?
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {INTEREST_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className={`ep-card px-4 py-3 cursor-pointer flex items-center gap-3 ${
                      data.interest === opt ? "border-[#00F22E]/50" : ""
                    }`}
                    data-testid={`interest-${opt}`}
                  >
                    <input
                      type="radio"
                      name="interest"
                      value={opt}
                      checked={data.interest === opt}
                      onChange={() => set("interest", opt)}
                      className="accent-[#00F22E]"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
              {errors.interest && (
                <p className="mt-3 text-sm text-red-400" data-testid="form-error-interest" role="alert">{errors.interest}</p>
              )}
            </fieldset>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <Row2>
                <Field label="Ad Soyad" required error={errors.fullName}>
                  <input value={data.fullName} onChange={(e) => set("fullName", e.target.value)} className="ep-input" placeholder="Adınız Soyadınız" data-testid="input-fullName" />
                </Field>
                <Field label="İşyeri Adı" required error={errors.memberBusinessName}>
                  <input value={data.memberBusinessName} onChange={(e) => set("memberBusinessName", e.target.value)} className="ep-input" placeholder="İşyerinizin adı" data-testid="input-memberBusinessName" />
                </Field>
              </Row2>
              <Row2>
                <Field label="Telefon" required error={errors.phone}>
                  <input inputMode="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} className="ep-input" placeholder="05XX XXX XX XX" data-testid="input-phone" />
                </Field>
                <Field label="E-posta" required error={errors.email}>
                  <input inputMode="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="ep-input" placeholder="ornek@ornek.com" data-testid="input-email" />
                </Field>
              </Row2>
              <Row2>
                <Field label="İl" required error={errors.city}>
                  <input value={data.city} onChange={(e) => set("city", e.target.value)} className="ep-input" data-testid="input-city" />
                </Field>
                <Field label="İlçe">
                  <input value={data.district} onChange={(e) => set("district", e.target.value)} className="ep-input" data-testid="input-district" />
                </Field>
              </Row2>
              <Field label="Sektör" required error={errors.sector}>
                <select value={data.sector} onChange={(e) => set("sector", e.target.value)} className="ep-input" data-testid="input-sector">
                  <option value="">Seçin</option>
                  {SECTOR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Mevcut Satış Kanalları">
                <ChipGroup options={CHANNEL_OPTIONS} values={data.currentChannels} onToggle={(v) => toggleArr("currentChannels", v)} testId="currentChannels" />
              </Field>
              <Field label="İlgilenilen Hizmetler">
                <ChipGroup options={SERVICE_OPTIONS} values={data.services} onToggle={(v) => toggleArr("services", v)} testId="services" />
              </Field>
              <Row2>
                <Field label="Yaklaşık Ürün Sayısı">
                  <input value={data.approxProducts} onChange={(e) => set("approxProducts", e.target.value)} className="ep-input" placeholder="Örn. 500" data-testid="input-approxProducts" />
                </Field>
                <Field label="Günlük Yaklaşık Sipariş Sayısı">
                  <input value={data.approxDailyOrders} onChange={(e) => set("approxDailyOrders", e.target.value)} className="ep-input" placeholder="Örn. 40" data-testid="input-approxDailyOrders" />
                </Field>
              </Row2>
              <Field label="Açıklama">
                <textarea value={data.description} onChange={(e) => set("description", e.target.value)} rows={4} className="ep-input" data-testid="input-description" />
              </Field>
            </div>
          )}

          {steps[step]?.key === "branch" && (
            <div className="space-y-6">
              {needsCourierBranch && (
                <div className="space-y-4">
                  <h3 className="font-display font-semibold text-lg">Kurye Çözümü Detayları</h3>
                  <Row2>
                    <Field label="Ortalama Teslimat Mesafesi">
                      <input value={data.avgDeliveryDistance} onChange={(e) => set("avgDeliveryDistance", e.target.value)} className="ep-input" placeholder="Örn. 3 km" data-testid="input-avgDeliveryDistance" />
                    </Field>
                    <Field label="Günlük Yaklaşık Kurye İhtiyacı">
                      <input value={data.dailyCourierNeed} onChange={(e) => set("dailyCourierNeed", e.target.value)} className="ep-input" placeholder="Örn. 2" data-testid="input-dailyCourierNeed" />
                    </Field>
                  </Row2>
                  <Field label="Kullanılacak Sipariş Kanalları">
                    <ChipGroup options={CHANNEL_OPTIONS} values={data.courierOrderChannels} onToggle={(v) => toggleArr("courierOrderChannels", v)} testId="courierOrderChannels" />
                  </Field>
                </div>
              )}
              {needsWebMobileBranch && (
                <div className="space-y-4">
                  <h3 className="font-display font-semibold text-lg">Web ve Mobil Sipariş Detayları</h3>
                  <Row2>
                    <Field label="Web Sitesi Mevcut mu?">
                      <select value={data.hasWebsite} onChange={(e) => set("hasWebsite", e.target.value)} className="ep-input" data-testid="input-hasWebsite">
                        <option value="">Seçin</option>
                        <option>Evet</option>
                        <option>Hayır</option>
                      </select>
                    </Field>
                    <Field label="Mobil Uygulama İsteniyor mu?">
                      <select value={data.wantsMobileApp} onChange={(e) => set("wantsMobileApp", e.target.value)} className="ep-input" data-testid="input-wantsMobileApp">
                        <option value="">Seçin</option>
                        <option>Evet</option>
                        <option>Hayır</option>
                      </select>
                    </Field>
                  </Row2>
                  <Field label="Tercih Edilen Ödeme Yöntemleri">
                    <ChipGroup options={PAYMENT_OPTIONS} values={data.paymentMethods} onToggle={(v) => toggleArr("paymentMethods", v)} testId="paymentMethods" />
                  </Field>
                  <Field label="Kurye Çözümüne İhtiyaç Var mı?">
                    <select value={data.needsCourier} onChange={(e) => set("needsCourier", e.target.value)} className="ep-input" data-testid="input-needsCourier">
                      <option value="">Seçin</option>
                      <option>Evet</option>
                      <option>Hayır</option>
                    </select>
                  </Field>
                </div>
              )}
            </div>
          )}

          {isLast && (
            <div className="space-y-4">
              <label className="flex items-start gap-3 text-sm text-white/95 cursor-pointer">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#00F22E]" checked={data.kvkkConsent} onChange={(e) => set("kvkkConsent", e.target.checked)} data-testid="input-kvkkConsent" />
                <span>
                  <Link to="/yasal/kvkk" className="text-[#00F22E] hover:underline" target="_blank">KVKK Aydınlatma Metni</Link>'ni okudum ve kişisel verilerimin başvurumun değerlendirilmesi amacıyla işlenmesini kabul ediyorum. <span className="text-[#00F22E]">*</span>
                </span>
              </label>
              {errors.kvkkConsent && <p className="text-sm text-red-400" data-testid="form-error-kvkk" role="alert">{errors.kvkkConsent}</p>}
              <label className="flex items-start gap-3 text-sm text-white/85 cursor-pointer">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#00F22E]" checked={data.commercialConsent} onChange={(e) => set("commercialConsent", e.target.checked)} data-testid="input-commercialConsent" />
                <span>Kampanya, tanıtım ve bilgilendirme amacıyla tarafıma ticari elektronik ileti gönderilmesini kabul ediyorum. (İsteğe bağlı)</span>
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            <button type="button" onClick={back} disabled={step === 0} className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="form-back">
              <ArrowLeft className="h-4 w-4" /> Önceki
            </button>
            {!isLast ? (
              <button type="submit" className="btn-primary" data-testid="form-next">
                Sonraki <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-70" data-testid="form-submit">
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Gönderiliyor</>) : (<>Gönder <ArrowRight className="h-4 w-4" /></>)}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

function Row2({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="text-sm text-white/90">
        {label} {required && <span className="text-[#00F22E]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs text-red-400" data-testid={`form-error-${label.replace(/\s+/g, "-").toLowerCase()}`} role="alert">{error}</span>}
    </label>
  );
}

function ChipGroup({ options, values, onToggle, testId }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = values.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            data-testid={`${testId}-${o}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              active
                ? "bg-[#00F22E]/12 border-[#00F22E] text-[#00F22E]"
                : "bg-white/[0.02] border-white/10 text-white/85 hover:border-white/30"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}
